import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  ASSESSMENT_ID_HEADER,
  CLIENT_ID_HEADER,
  isHeaderSkippedByPolicy,
} from '../api/interceptors/header-injection.context';
import { FHIR_API_BASE_URL } from './api-client.tokens';
import { RequestTrackingContextService } from './request-tracking-context.service';

function isFhirRequest(url: string, baseUrl: string | null | undefined): boolean {
  if (!baseUrl) {
    return false;
  }

  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const pathPart = url.replace(/^https?:\/\/[^/]+/, '') || url;

  return (
    pathPart === normalizedBase ||
    pathPart.startsWith(`${normalizedBase}/`) ||
    url === normalizedBase ||
    url.startsWith(`${normalizedBase}/`)
  );
}

/**
 * RequestTrackingInterceptor
 *
 * Attaches `X-Client-Id`/`X-Assessment-Id` headers (when set via `RequestTrackingContextService`) to outgoing API
 * requests, so backend logs for the duration of an in-progress assessment can be grouped by ClientId/AssessmentId
 * alongside the session's `X-Correlation-ID`.
 */
export const requestTrackingInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const fhirBaseUrl = inject(FHIR_API_BASE_URL, { optional: true });
  if (isFhirRequest(req.url, fhirBaseUrl)) {
    return next(req);
  }

  const trackingContext = inject(RequestTrackingContextService);
  const clientId = trackingContext.clientId();
  const assessmentId = trackingContext.assessmentId();

  if (clientId == null && assessmentId == null) {
    return next(req);
  }

  const setHeaders: Record<string, string> = {};

  if (
    clientId != null &&
    !req.headers.has(CLIENT_ID_HEADER) &&
    !isHeaderSkippedByPolicy(req.context, CLIENT_ID_HEADER)
  ) {
    setHeaders[CLIENT_ID_HEADER] = String(clientId);
  }

  if (
    assessmentId != null &&
    !req.headers.has(ASSESSMENT_ID_HEADER) &&
    !isHeaderSkippedByPolicy(req.context, ASSESSMENT_ID_HEADER)
  ) {
    setHeaders[ASSESSMENT_ID_HEADER] = String(assessmentId);
  }

  if (Object.keys(setHeaders).length === 0) {
    return next(req);
  }

  return next(req.clone({ setHeaders }));
};
